import { useEffect } from "react";

const SITE_URL = "https://taxinearme.it";
export const TAXINEARME_SITE_URL = SITE_URL;

/**
 * Inject one or more <script type="application/ld+json"> blocks into <head>.
 * Cleans up on unmount / when `blocks` changes.
 *
 * @param {Array<{ id: string, data: object | string | null | undefined }>} blocks
 */
export function useJsonLd(blocks) {
  useEffect(() => {
    const created = [];
    (blocks || []).forEach(({ id, data }) => {
      if (!id || !data) return;
      const payload = typeof data === "string" ? data : JSON.stringify(data);
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("id", id);
        document.head.appendChild(el);
        created.push(id);
      }
      el.textContent = payload;
    });
    return () => {
      created.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [blocks]);
}

/** Homepage: WebSite + Organization/LocalBusiness. */
export function buildHomeJsonLd({ whatsappNumber, siteUrl = SITE_URL } = {}) {
  const base = siteUrl.replace(/\/$/, "");
  return [
    {
      id: "tnm-jsonld-website",
      data: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "TaxiNearMe",
        url: `${base}/`,
        potentialAction: {
          "@type": "SearchAction",
          target: `${base}/routes?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    },
    {
      id: "tnm-jsonld-organization",
      data: {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `${base}/#business`,
        name: "TaxiNearMe",
        url: `${base}/`,
        logo: `${base}/logo.png`,
        image: `${base}/logo.png`,
        telephone: whatsappNumber || "+39 393 907 8663",
        priceRange: "€€",
        areaServed: [
          { "@type": "AdministrativeArea", name: "Venice" },
          { "@type": "AdministrativeArea", name: "Veneto" },
          { "@type": "Country", name: "Italy" },
        ],
        sameAs: [],
      },
    },
  ];
}

/** /routes listing: BreadcrumbList + ItemList of every route. */
export function buildRoutesListJsonLd({ routes, siteUrl = SITE_URL } = {}) {
  const base = siteUrl.replace(/\/$/, "");
  const items = (routes || [])
    .filter((r) => r && r.slug)
    .map((r, idx) => {
      const origin = collapseVenice(r.origin);
      const destination = collapseVenice(r.destination);
      return {
        "@type": "ListItem",
        position: idx + 1,
        url: `${base}/route/${r.slug}`,
        name: `${origin} to ${destination} Private Transfer`,
      };
    });
  return [
    {
      id: "tnm-jsonld-breadcrumbs",
      data: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
          { "@type": "ListItem", position: 2, name: "Routes", item: `${base}/routes` },
        ],
      },
    },
    {
      id: "tnm-jsonld-itemlist",
      data: {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "TaxiNearMe Routes",
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        numberOfItems: items.length,
        itemListElement: items,
      },
    },
  ];
}

/** Route landing page: BreadcrumbList (Home → Routes → [Origin → Destination]). */
export function buildRouteBreadcrumbJsonLd({ route, siteUrl = SITE_URL } = {}) {
  if (!route || !route.slug) return [];
  const base = siteUrl.replace(/\/$/, "");
  const origin = collapseVenice(route.origin);
  const destination = collapseVenice(route.destination);
  return [
    {
      id: "tnm-jsonld-breadcrumbs",
      data: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
          { "@type": "ListItem", position: 2, name: "Routes", item: `${base}/routes` },
          {
            "@type": "ListItem",
            position: 3,
            name: `${origin} to ${destination}`,
            item: `${base}/route/${route.slug}`,
          },
        ],
      },
    },
  ];
}

/** Collapse the 4 Venice DB variants into the customer-friendly "Venice" label. */
function collapseVenice(loc) {
  if (!loc) return "";
  const v = String(loc).trim();
  if (
    v === "Venice Airport" ||
    v === "Venice - Piazzale Roma" ||
    v === "Venice - Near Venice" ||
    v === "Venice - Water Taxi"
  ) {
    return "Venice";
  }
  return v;
}
